<script lang="ts" setup>
import {
   ContractTemplateFieldName,
   ContractTemplateFieldNameKey,
} from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import {
   ContractTemplateCalculatedValue,
   ContractTemplateCondition,
   ContractTemplateField,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { FormType } from '@/core/models/form-type.enum';
import { NxtBadge } from '@nxtpeople/nxt-styles';
import { getPropertyByKey } from '@nxtpeople/nxt-vue-library';
import { inject, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const emits = defineEmits<{
   (e: 'openCalculatedFieldsModal'): void;
}>();
const props = defineProps({
   field: { type: Object as PropType<ContractTemplateField>, default: null },
   fieldName: { type: Object as PropType<ContractTemplateFieldName>, default: null },
   calculatedValues: { type: Array as PropType<Array<ContractTemplateCalculatedValue>>, default: () => [] },
   options: { type: Array, default: null },
});
const { field, fieldName, options, calculatedValues } = toRefs(props);

//#region overflow
const tagsContainer = ref<HTMLDivElement>();
const ghostTags = ref<HTMLDivElement>();
const amountOfTagsAvailable = ref(calculatedValues.value.length);
const activeTabKey = inject<Ref<string>>('activeTabKey', ref(''));

watch(
   activeTabKey,
   async () => {
      setTimeout(() => {
         if (!tagsContainer.value) return;
         const tagsContainerWidth = tagsContainer.value?.clientWidth || 0;

         const tags = Array.from(ghostTags.value!.querySelectorAll('.contract-template-tags__tag') || []);

         const largestTag = Math.max(...tags.map((t) => t.clientWidth));
         const overflowWidth = 40;

         amountOfTagsAvailable.value = Math.floor((tagsContainerWidth - overflowWidth) / largestTag) || 0;
      }, 200);
   },
   { immediate: true, flush: 'post' },
);
//#endregion

//#region calculated fields
function getCalculatedValueDisplay(calculatedValue: ContractTemplateCalculatedValue) {
   if (fieldName.value.key === ContractTemplateFieldNameKey.Color) {
      return fieldName.value.options?.find((o) => o === calculatedValue.value);
   }

   if (fieldName.value.form_type === FormType.Dropdown) {
      const option = options.value?.find((o: any) => o.id === calculatedValue.value);
      if (!option) return '';
      return getPropertyByKey(fieldName.value?.display_key || 'name', option);
   }

   if (fieldName.value.form_type === FormType.DropdownSearch && calculatedValue.selected_value) {
      return getPropertyByKey(fieldName.value?.display_key || 'name', calculatedValue.selected_value);
   }

   if (fieldName.value.key === ContractTemplateFieldNameKey.Salary) return `€${calculatedValue.value}`;

   return calculatedValue.value;
}

function getConditionDisplay(condition: ContractTemplateCondition) {
   const formType = condition.calculated_field_condition_type?.contract_template_field_type?.form_type;

   if (formType === FormType.NumberRange) {
      return `${t('generic.min')} ${condition.min_value} ${t('generic.and')} ${t('generic.max')} ${condition.max_value} ${t('generic.is')}`;
   }

   if (formType === FormType.Dropdown) {
      const value =
         typeof condition.selected_value?.name === 'object'
            ? condition.selected_value?.name?.[locale.value]
            : condition.selected_value?.name;
      return `${value} ${t('generic.is')}`;
   }

   if (formType === FormType.DropdownSearch) {
      const value =
         typeof condition.selected_value?.name === 'object'
            ? condition.selected_value?.name?.[locale.value]
            : condition.selected_value?.name;
      return `${value} ${t('generic.is')}`;
   }

   return '';
}

function removeCalculatedValue(index: number) {
   field.value.calculated_values.splice(index, 1);
}
//#endregion
</script>

<template>
   <div :id="`tag-${fieldName.key}`" ref="tagsContainer" :class="'contract-template-tags'">
      <div class="d-flex gap-2 align-items-center">
         <NxtHoverCard
            v-for="(calculatedValue, calculatedValueIndex) in calculatedValues.slice(0, amountOfTagsAvailable)"
            :key="calculatedValueIndex"
            :with-triangle="false"
            :is-card-hoverable="true"
         >
            <template #hover-item>
               <NxtBadge
                  class="contract-template-tags__tag"
                  :type="'grey'"
                  :style="{ minWidth: fieldName.form_type === FormType.Color ? 'unset' : '5.38rem' }"
                  @click="emits('openCalculatedFieldsModal')"
               >
                  <span v-if="fieldName.key !== ContractTemplateFieldNameKey.Color" style="font-size: 1rem" class="me-2">
                     {{ getCalculatedValueDisplay(calculatedValue) }}
                  </span>
                  <!-- Color -->
                  <div
                     v-else
                     class="me-2"
                     :style="{
                        height: '0.77rem',
                        width: '0.77rem',
                        backgroundColor: calculatedValue.value.startsWith('#') ? calculatedValue.value : `#${calculatedValue.value}`,
                        display: 'inline-block',
                     }"
                  ></div>
                  <i
                     :title="t('actions.delete')"
                     style="font-size: 0.9rem"
                     class="fas fa-times"
                     @click.stop="removeCalculatedValue(calculatedValueIndex)"
                  ></i>
               </NxtBadge>
            </template>
            <div>
               <p class="mt-0">
                  <strong> {{ getCalculatedValueDisplay(calculatedValue) }}:</strong>
               </p>
               <p v-for="(condition, conditionIndex) in calculatedValue.conditions" :key="conditionIndex" class="m-0">
                  <strong class="me-1">{{ conditionIndex === 0 ? t('generic.if') : t('generic.and') }}</strong>
                  <span v-lowercase class="me-1">{{ condition?.calculated_field_condition_type?.name }}</span>
                  <span v-lowercase> {{ getConditionDisplay(condition) }} </span>
               </p>
            </div>
         </NxtHoverCard>
      </div>

      <div v-if="calculatedValues.length > amountOfTagsAvailable && calculatedValues.length" class="contract-template-tags__overflow">
         <strong style="color: var(--text-disabled)" class="m-0">...</strong>
         <NxtBadge :type="'grey'" class="contract-template-tags__count py-1" @click="emits('openCalculatedFieldsModal')">
            <span style="font-size: 1rem">
               {{ calculatedValues.length || 0 }}
            </span>
         </NxtBadge>
      </div>

      <div ref="ghostTags" class="contract-template-tags__ghost d-flex gap-2 align-items-center">
         <NxtBadge
            v-for="(calculatedValue, calculatedValueIndex) in calculatedValues"
            :key="calculatedValueIndex"
            class="contract-template-tags__tag"
            :type="'grey'"
            :style="{ minWidth: fieldName.form_type === FormType.Color ? 'unset' : '5.38rem' }"
            @click="emits('openCalculatedFieldsModal')"
         >
            <span v-if="fieldName.key !== ContractTemplateFieldNameKey.Color" style="font-size: 1rem" class="me-2">
               {{ getCalculatedValueDisplay(calculatedValue) }}
            </span>
            <div
               v-else
               class="me-2"
               :style="{
                  height: '0.77rem',
                  width: '0.77rem',
                  backgroundColor: calculatedValue.value.startsWith('#') ? calculatedValue.value : `#${calculatedValue.value}`,
                  display: 'inline-block',
               }"
            ></div>
            <i
               :title="t('actions.delete')"
               style="font-size: 0.9rem"
               class="fas fa-times"
               @click.stop="removeCalculatedValue(calculatedValueIndex)"
            ></i>
         </NxtBadge>
      </div>
   </div>
</template>

<style lang="scss">
.contract-template-tags {
   position: relative;
   display: flex;
   justify-content: space-between;
   align-items: center;
   overflow: hidden;
   gap: var(--nxt-gutter-small);
   width: 81%;

   > * {
      min-height: toRem(20);
   }
}

.contract-template-tags__ghost {
   opacity: 0;
   z-index: -1;
   position: absolute;
}

.contract-template-tags__tag {
   display: grid;
   grid-template-columns: 1fr 0.77rem;
   align-items: center;
   padding: 0.2rem var(--nxt-gutter-small);
   cursor: pointer;

   min-width: toRem(70);
   max-width: toRem(150);

   > * {
      &:first-child {
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;
      }
   }

   .fa-times {
      min-width: 0.77rem;
   }
}
.contract-template-tags__overflow {
   display: flex;
   align-items: center;
   justify-content: space-between;
   width: toRem(40);
   gap: var(--nxt-gutter-small);
}
</style>
